// Landing page for a Settings group (Organization / Policies / Access).
// Renders the group's permitted children as a grid of cards; each card opens
// the child's existing /settings/{id} page.

import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useAccess } from '../../contexts/AccessContext';
import {
  SETTINGS_GROUPS,
  visibleChildren,
  type SettingsGroupId,
} from '../../config/settingsNav';

interface SettingsGroupPageProps {
  groupId: SettingsGroupId;
}

export default function SettingsGroupPage({ groupId }: SettingsGroupPageProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasAccess, isPathVisible } = useAccess();

  const group = SETTINGS_GROUPS[groupId];
  const children = visibleChildren(group, {
    hasAccess,
    isPathVisible,
    role: user?.role || 'admin',
  });

  return (
    <div className="max-w-5xl">
      <div className="mb-6">
        <h1 className="text-[22px] font-semibold text-[#1a1a1a] leading-tight tracking-[-0.01em]">
          {group.label}
        </h1>
        <p className="text-sm text-gray-500 mt-1">{group.description}</p>
      </div>

      {children.length === 0 ? (
        <p className="text-sm text-gray-500">Nothing here you can access.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {children.map((leaf) => {
            const Icon = leaf.icon;
            return (
              <button
                key={leaf.id}
                onClick={() => navigate(`/settings/${leaf.id}`)}
                className="group flex items-start gap-3 text-left bg-white border border-gray-200 rounded-xl p-4 hover:border-gray-300 hover:bg-gray-50 transition-colors"
              >
                <div className="p-2 rounded-lg bg-blue-50 text-blue-600 shrink-0">
                  <Icon size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[15px] font-semibold text-gray-900">{leaf.label}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{leaf.description}</div>
                </div>
                <ChevronRight
                  size={16}
                  className="text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity mt-1 shrink-0"
                />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
